import { useQueries } from '@tanstack/react-query'
import { useApiClient } from '../useApiClient'
import { walkAbsolute, walkRatioAbsolute, type WalkAbsoluteRow } from '../endpoints/walks'
import { clampFromToFloor, useModelFloorIso } from '~/state/modelFloor'

export interface FanoutTimeframe {
  id: string
  label: string
  from: string
  to: string
  /** Campaigns C1 Task 7: per-arm override — a campaign arm's merged+fact-pruned predicate.
   *  Replaces spec.filter_dims for THIS arm's walk only; absent = spec.filter_dims (today's
   *  behavior, unchanged). */
  filter_dims?: Record<string, string[]>
}

export type WalkSpec =
  | {
      family: 'ratio_absolute'
      kpi: string
      filter_dims: Record<string, string[]>
      group_by: string[]
    }
  | {
      family: 'absolute'
      kpi: string
      fact_table: string
      filter_dims: Record<string, string[]>
      group_by: string[]
    }

export interface FanoutSlice {
  timeframe: FanoutTimeframe
  rows: WalkAbsoluteRow[]
  isLoading: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  isError: boolean
  error: Error | null
}

export interface TimeframeFanout {
  byTimeframe: FanoutSlice[]
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  isPlaceholderData: boolean
}

export function useTimeframeFanout(spec: WalkSpec, timeframes: FanoutTimeframe[]): TimeframeFanout {
  const client = useApiClient()
  const floorIso = useModelFloorIso()
  const results = useQueries({
    queries: timeframes.map((tf) => {
      const tfFrom = clampFromToFloor(tf.from, floorIso)
      // Campaigns C1 Task 7: a per-arm predicate overrides the spec's filters for this arm only.
      const filterDims = tf.filter_dims ?? spec.filter_dims
      return {
        // tf.filter_dims rides the key — same-window arms with different predicates (e.g. two
        // campaigns compared over identical dates) must not share a cache entry.
        queryKey: ['timeframe_fanout', spec, tfFrom, tf.to, tf.filter_dims ?? null] as const,
        // FU-28 (latent variant): both fanout walks parse to value-only rows so a cross-family
        // placeholder cannot crash today — but it would silently render foreign-family figures.
        // Same family-keyed rule as useDynamicsPanels.
        placeholderData: (
          prev: WalkAbsoluteRow[] | undefined,
          prevQuery?: { queryKey: readonly unknown[] },
        ) =>
          (prevQuery?.queryKey[1] as WalkSpec | undefined)?.family === spec.family
            ? prev
            : undefined,
        queryFn: () =>
          spec.family === 'ratio_absolute'
            ? walkRatioAbsolute(client, {
                kpi: spec.kpi,
                from: tfFrom,
                to: tf.to,
                filter_dims: filterDims,
                group_by: spec.group_by,
              })
            : walkAbsolute(client, {
                fact_table: spec.fact_table,
                kpi: spec.kpi,
                from: tfFrom,
                to: tf.to,
                filter_dims: filterDims,
                group_by: spec.group_by,
              }),
      }
    }),
  })

  const byTimeframe: FanoutSlice[] = timeframes.map((tf, i) => {
    const r = results[i]
    return {
      timeframe: tf,
      rows: r?.data ?? [],
      isLoading: r?.isLoading ?? false,
      isFetching: r?.isFetching ?? false,
      isPlaceholderData: r?.isPlaceholderData ?? false,
      isError: r?.isError ?? false,
      error: (r?.error as Error | null) ?? null,
    }
  })

  return {
    byTimeframe,
    isLoading: byTimeframe.some((s) => s.isLoading),
    isFetching: byTimeframe.some((s) => s.isFetching),
    isError: byTimeframe.some((s) => s.isError),
    isPlaceholderData: byTimeframe.some((s) => s.isPlaceholderData),
  }
}
